import React, { useEffect, useState } from "react";
import { supabase, fmtBRL, formatError } from "@/lib/supabase";
import { db } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const STATUS = {
  pago: { label: "Pago", cls: "bg-green-100 text-green-800" },
  pendente: { label: "Pendente", cls: "bg-yellow-100 text-yellow-800" },
};
const TIPO = { entrada: "Entrada", saida: "Saída" };

const CATEGORIAS_ENTRADA = [
  "Revisão Acadêmica", "Revisão de TCC", "Revisão ABNT", "Correção de Textos Acadêmicos", 
  "Revisão Literária", "Revisor de Livros", "Aulas de Inglês Online", "Aulas de Inglês para Iniciantes",
  "Aulas de Português", "Produção Textual Profissional", "Mentoria Textual", "Mentoria de Escrita",
  "Consultoria Linguística Profissional", "Publicação de Livro", "Redação", "Outros Serviços"
];

const CATEGORIAS_SAIDA = [
  "Imposto", "Despesa Pessoal", "Tecnologia", "Equipamentos", "Hospedagem do Site", "Marketing",
  "Investimentos - Reserva para Manutenção", "Investimentos - Reserva de Previsão de Gastos",
  "Cursos e Formações", "Outros"
];

export default function Finance() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().substring(0, 7));
  const [form, setForm] = useState({ descricao: "", categoria: "", valor: 0, tipo: "entrada", status: "pendente", data: new Date().toISOString().split("T")[0] });

  const reload = async () => {
    try { setItems((await db.select("financeiro", { order: "created_at" })) || []); }
    catch (err) { toast.error(formatError(err)); }
  };
  useEffect(() => { if (user) reload(); }, [user]);

  const save = async () => {
    try {
      if (editingId) {
        await db.update("financeiro", editingId, { ...form, valor: Number(form.valor) });
        toast.success("Lançamento atualizado");
      } else {
        await db.insert("financeiro", { ...form, valor: Number(form.valor), user_id: user.id });
        toast.success("Lançamento criado");
      }
      setOpen(false); reload();
    } catch (err) { toast.error(formatError(err)); }
  };
  const handleEdit = (it) => {
    setForm({ descricao: it.descricao, categoria: it.categoria || "", valor: it.valor, tipo: it.tipo, status: it.status, data: it.data || "" });
    setEditingId(it.id);
    setOpen(true);
  };
  const upd = async (id, s) => { try { await db.update("financeiro", id, { status: s }); reload(); } catch (err) { toast.error(formatError(err)); } };
  const rm = async (id) => { try { await db.remove("financeiro", id); reload(); } catch (err) { toast.error(formatError(err)); } };

  const filteredItems = items.filter(it => !it.data || it.data.startsWith(currentMonth));

  const totals = filteredItems.reduce((acc, i) => {
    const v = Number(i.valor) || 0;
    if (i.tipo === "entrada") {
      if (i.status === "pago") acc.recebido += v;
      else acc.aReceber += v;
    } else {
      if (i.status === "pago") acc.pago += v;
      else acc.aPagar += v;
    }
    return acc;
  }, { recebido: 0, aReceber: 0, pago: 0, aPagar: 0 });

  return (
    <div data-testid="finance-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Financeiro</div>
          <h1 className="font-serif text-4xl md:text-5xl">Entradas & saídas</h1>
          <p className="text-[#4A4A4A] mt-2">Controle seu caixa com clareza.</p>
        </div>
        <div className="flex items-end gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-tighter text-[#8A8A8A] mb-1">Mês</div>
            <Select value={currentMonth} onValueChange={setCurrentMonth}>
              <SelectTrigger className="rounded-none w-48 bg-white border border-black/10 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 60 }).map((_, i) => {
                  const d = new Date();
                  d.setMonth(d.getMonth() - i);
                  const val = d.toISOString().substring(0, 7);
                  const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                  return <SelectItem key={val} value={val} className="capitalize">{label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={open} onOpenChange={(v) => { if (!v) { setForm({ descricao: "", categoria: "", valor: 0, tipo: "entrada", status: "pendente", data: new Date().toISOString().split("T")[0] }); setEditingId(null); } setOpen(v); }}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white h-10" data-testid="new-invoice-btn" onClick={() => { setForm({ descricao: "", categoria: "", valor: 0, tipo: "entrada", status: "pendente", data: new Date().toISOString().split("T")[0] }); setEditingId(null); }}>
                <Plus className="w-4 h-4 mr-2" /> Novo lançamento
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editingId ? "Editar lançamento" : "Novo lançamento"}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <div><Label className="text-xs font-mono uppercase">Descrição</Label>
                <Input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="rounded-none" data-testid="fin-descricao" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-mono uppercase">Data</Label>
                  <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="rounded-none" /></div>
                <div><Label className="text-xs font-mono uppercase">Valor (R$)</Label>
                  <Input type="number" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className="rounded-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-mono uppercase">Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v, categoria: "" })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TIPO).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs font-mono uppercase">Categoria</Label>
                  <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {(form.tipo === 'entrada' ? CATEGORIAS_ENTRADA : CATEGORIAS_SAIDA).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-mono uppercase">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <Button onClick={save} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white w-full" data-testid="fin-save-btn">Salvar</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card className="p-6 bg-[#1A1A1A] text-white rounded-none">
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Recebido</div>
          <div className="font-serif text-3xl">{fmtBRL(totals.recebido)}</div>
        </Card>
        <Card className="p-6 bg-white border border-black/10 rounded-none">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-2">A receber</div>
          <div className="font-serif text-3xl">{fmtBRL(totals.aReceber)}</div>
        </Card>
        <Card className="p-6 bg-white border border-black/10 rounded-none">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-2">Pago (saídas)</div>
          <div className="font-serif text-3xl">{fmtBRL(totals.pago)}</div>
        </Card>
        <Card className="p-6 bg-white border border-black/10 rounded-none">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-2">A pagar</div>
          <div className="font-serif text-3xl text-[#D9381E]">{fmtBRL(totals.aPagar)}</div>
        </Card>
      </div>

      {filteredItems.length === 0 ? (
        <Card className="p-16 text-center border border-black/10 rounded-none bg-white">
          <p className="text-[#8A8A8A] font-mono">Nenhum lançamento neste mês.</p>
        </Card>
      ) : (
        <Card className="border border-black/10 rounded-none bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#EFECE6] text-xs font-mono uppercase tracking-widest text-left">
              <tr><th className="p-4">Data</th><th className="p-4">Descrição & Categoria</th><th className="p-4">Tipo</th><th className="p-4">Valor</th><th className="p-4">Status</th><th className="p-4 text-right">Ações</th></tr>
            </thead>
            <tbody>
              {filteredItems.map((it) => (
                <tr key={it.id} className="border-t border-black/10" data-testid={`fin-${it.id}`}>
                  <td className="p-4 font-mono text-xs">{it.data ? new Date(it.data + "T12:00:00").toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="p-4 font-serif text-base">
                    <div>{it.descricao}</div>
                    {it.categoria && <div className="text-xs font-sans text-[#8A8A8A] mt-1 bg-black/5 px-2 py-0.5 inline-block rounded">{it.categoria}</div>}
                  </td>
                  <td className="p-4 font-mono uppercase text-xs">{TIPO[it.tipo] || it.tipo}</td>
                  <td className={`p-4 font-mono ${it.tipo === "saida" ? "text-[#D9381E]" : ""}`}>{it.tipo === "saida" ? "− " : ""}{fmtBRL(it.valor)}</td>
                  <td className="p-4">
                    <Select value={it.status} onValueChange={(v) => upd(it.id, v)}>
                      <SelectTrigger className="rounded-none w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 flex items-center justify-end gap-3">
                    <button onClick={() => handleEdit(it)} className="text-[#8A8A8A] hover:text-black"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => rm(it.id)} className="text-[#D9381E] hover:text-[#B82914]"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
